import { HL7AckProcessing } from '@lmt/shared-types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { FieldBlock } from '@lmt/unicorn-shared/app/components/common/FieldBlock';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { ToggleButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/ToggleButton';
import { useSubtenants } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import {
  maxRule,
  minRule,
  positiveRule,
  requiredRule,
} from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { HL7SettingsFormProps } from './types';
import { getEnumValuesDict } from '../../../patients/list/common/types';

import styles from './../SettingsBlock.module.scss';
import { Input } from '../../../common/ui-kit/inputs/Input';
import { characterEncodings } from './common';

export const HL7SettingsBlock: FC<HL7SettingsFormProps> = (props) => {
  const i18next = useScopedTranslation('tenant_settings.hl7.general');

  const ackValues = useMemo(
    () => getEnumValuesDict(HL7AckProcessing, 'hl_7_ack_processing'),
    [],
  );
  const { isCleanupJobGloballyEnabled } = props;

  const { register, control, formState, watch } = props.form;

  const subtenants = useSubtenants();
  const [cleanupEnabled] = watch(['cleanupEnabled']);

  return (
    <div className={styles.blockContainer}>
      <FieldBlock title={i18next.t('general_settings')}>
        <CheckBox
          title={i18next.t('only_process_existing_patients')}
          className={styles.checkbox}
          {...register('onlyProcessMessagesForExistingPatients')}
        />
        <Field title={i18next.t('default_subtenant')} className={styles.field}>
          <Controller
            control={control}
            name={'subtenantId'}
            render={({ field: { onChange, value } }) => (
              <div className={styles.input}>
                <DropDownInput
                  useIdFunctionAsValue={true}
                  options={subtenants}
                  value={value}
                  idFunction={'id'}
                  getOptionLabel={'name'}
                  required={true}
                  onValueChanged={(value) => {
                    onChange(value?.id);
                  }}
                  errorText={formState.errors.subtenantId?.message}
                />
              </div>
            )}
          />
        </Field>
        <Field title={i18next.t('ack_settings')} className={styles.field}>
          <Controller
            control={control}
            name={'ackProcessing'}
            render={({ field: { onChange, value } }) => (
              <div className={styles.input}>
                <DropDownInput
                  useIdFunctionAsValue={true}
                  options={ackValues}
                  value={value}
                  idFunction={'value'}
                  getOptionLabel={'label'}
                  required={true}
                  onValueChanged={(value) => {
                    onChange(value?.value);
                  }}
                  errorText={formState.errors.ackProcessing?.message}
                />
              </div>
            )}
          />
        </Field>
        <Field title={i18next.t('encoding')} className={styles.field}>
          <Controller
            control={control}
            name={'encoding'}
            render={({ field: { onChange, value } }) => (
              <div className={styles.input}>
                <DropDownInput
                  options={characterEncodings}
                  required={true}
                  value={value}
                  onValueChanged={(value) => {
                    onChange(value);
                  }}
                  errorText={formState.errors.subtenantId?.message}
                />
              </div>
            )}
          />
        </Field>
        <Field title={i18next.t('app_name')} className={styles.field}>
          <Input
            variant="normal"
            {...register('appName')}
            errorText={formState.errors.appName?.message}
          />
        </Field>
        <Field title={i18next.t('facility')} className={styles.field}>
          <Input
            variant="normal"
            {...register('facility')}
            errorText={formState.errors.facility?.message}
          />
        </Field>
        <Field className={styles.field} title={''}>
          <ToggleButton
            {...register('cleanupEnabled')}
            title={i18next.t('cleanup_enabled')}
            disabled={!isCleanupJobGloballyEnabled}
            hint={
              isCleanupJobGloballyEnabled
                ? null
                : i18next.t('cleanup_disabled_globally_hint')
            }
          />
        </Field>
        <Field
          title={i18next.t('delete_after_months')}
          className={styles.field}
        >
          <Input
            variant="normal"
            type="number"
            disabled={!isCleanupJobGloballyEnabled || !cleanupEnabled}
            {...register('deleteAfterMonths')}
            errorText={formState.errors.deleteAfterMonths?.message}
          />
        </Field>
      </FieldBlock>
    </div>
  );
};
